import {
  DAILY_SALES_DEFAULT_DAYS,
  DAILY_SALES_EARLIEST_DATE,
  DAILY_SALES_MAX_DAYS,
  easternDateOf,
} from '@bonsai/shared';

import type { DailySalesReport, SalesDateRange } from '../../../api/daily-sales';
import { addDays, daysBetween, weekdayOf } from '../../../shared/date-time-range/date-keys';

export type RangePreset = 'default' | 'lastWeek' | 'lastMonth' | 'custom';

export const RANGE_PRESETS: ReadonlyArray<{ value: RangePreset; label: string }> = [
  { value: 'default', label: `Last ${DAILY_SALES_DEFAULT_DAYS} days` },
  { value: 'lastWeek', label: 'Last week' },
  { value: 'lastMonth', label: 'Last month' },
  { value: 'custom', label: 'Custom' },
];

/** The same choices, short enough for four to share a phone's width. */
export const RANGE_PRESETS_SHORT: ReadonlyArray<{ value: RangePreset; label: string }> = [
  { value: 'default', label: `${DAILY_SALES_DEFAULT_DAYS} days` },
  ...RANGE_PRESETS.slice(1),
];

/** Today's calendar date in Eastern time, which is how the report counts days. */
export function salesToday(now: Date = new Date()): string {
  return easternDateOf(now);
}

/** How many days a range covers, counting both ends. */
export function daysIn({ from, to }: SalesDateRange): number {
  return daysBetween(from, to) + 1;
}

export function defaultRange(today: string): SalesDateRange {
  return { from: addDays(today, -(DAILY_SALES_DEFAULT_DAYS - 1)), to: today };
}

/** The Monday to Sunday before the week `today` is in. */
export function lastWeek(today: string): SalesDateRange {
  const weekday = weekdayOf(today);
  const lastSunday = addDays(today, -(weekday === 0 ? 7 : weekday));
  return { from: addDays(lastSunday, -6), to: lastSunday };
}

/** The calendar month before the one `today` is in. */
export function lastMonth(today: string): SalesDateRange {
  const lastDay = addDays(`${today.slice(0, 8)}01`, -1);
  return { from: `${lastDay.slice(0, 8)}01`, to: lastDay };
}

/** The dates a preset stands for; null for the default, which the address leaves out. */
export function presetRange(preset: Exclude<RangePreset, 'custom'>, today: string) {
  if (preset === 'lastWeek') return lastWeek(today);
  if (preset === 'lastMonth') return lastMonth(today);
  return null;
}

/** Which preset the address's dates match; anything else is a custom range. */
export function presetOf(range: SalesDateRange | null, today: string): RangePreset {
  if (!range) return 'default';
  const same = (r: SalesDateRange) => r.from === range.from && r.to === range.to;
  if (same(lastWeek(today))) return 'lastWeek';
  if (same(lastMonth(today))) return 'lastMonth';
  return 'custom';
}

/** What is wrong with typed custom dates, before asking the server; null when they are fine. */
export function customRangeProblem(range: SalesDateRange, today: string): string | null {
  if (!range.from || !range.to) return 'Pick both a start and an end date.';
  if (range.from > range.to) return 'The start date must be on or before the end date.';
  if (range.from < DAILY_SALES_EARLIEST_DATE) {
    return `The start date can't be before ${DAILY_SALES_EARLIEST_DATE}.`;
  }
  if (range.to > today) return "The end date can't be after today.";
  const days = daysIn(range);
  if (days > DAILY_SALES_MAX_DAYS) {
    return `Pick ${DAILY_SALES_MAX_DAYS} days or fewer. That's ${days}.`;
  }
  return null;
}

/** The chosen range from the address. A half-filled or garbled pair is passed on as it
 *  is, so the server can say what is wrong with it. */
export function rangeFromParams(params: URLSearchParams): SalesDateRange | null {
  const from = params.get('from');
  const to = params.get('to');
  if (!from && !to) return null;
  return { from: from ?? '', to: to ?? '' };
}

/**
 * The day retail numbers stop at, when no retail sale is recorded on either of the last
 * two full days the report covers. Shops sell nearly every day, so two silent days mean
 * the Shopify copy has stopped updating; one alone may be a day the shops were closed.
 */
export function retailStopsAt(report: DailySalesReport): string | null {
  if (!report.retailLatestSale) return null;
  const lastFullDay = report.to === report.today ? addDays(report.today, -1) : report.to;
  const latest = salesToday(new Date(report.retailLatestSale));
  return latest < addDays(lastFullDay, -1) ? latest : null;
}
